import type { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import type { Popup } from '../../../popups/Popup';
import { HowToPlayPopupLayout } from '../../../../layouts/popups/HowToPlayPopupLayout';
import { applyLayoutState, revokeLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import type { BoosterDefinitions } from '../../boosters/types';
import { BoosterType } from '../../boosters/types';
import { BoosterButtonLayout } from '../../../../layouts/boosters/BoosterButtonLayout';
import { getSafeAreaInset } from '../../../safearea/SafeAreaProvider';

interface HowToPlayPopupConfig {
  boostersDefinitions: BoosterDefinitions;
  levelNumber: number;
  closeButtonPositionSource?: Container;
}

export class HowToPlayPopup implements Popup {
  public onCloseClicked = new Signal();

  private readonly layout = new HowToPlayPopupLayout();

  public readonly root = this.layout.root;

  private boosterTab = true;

  constructor(config: HowToPlayPopupConfig) {
    const { boostersTabButton, howToPlayTabButton, closeButton } = this.layout;

    boostersTabButton.on('pointertap', () => {
      if (this.boosterTab) return;
      revokeLayoutState(this.layout, 'howToPlay');
      this.boosterTab = true;
    });

    howToPlayTabButton.on('pointertap', () => {
      if (!this.boosterTab) return;
      applyLayoutState(this.layout, 'howToPlay');
      this.boosterTab = false;
    });

    closeButton.on('pointertap', () => {
      this.onCloseClicked.emit();
    });

    this.initBoosterViews(config);
  }

  public resize(): void {
    const safeArea = getSafeAreaInset();
    this.root.props({
      x: safeArea.left,
      y: (this.root.pixi.renderer.height * -0.5) + safeArea.top,
    });

    this.layout.background.props({
      y: -safeArea.top,
      height: this.layout.background.height + safeArea.top,
    });
  }

  private initBoosterViews(config: HowToPlayPopupConfig): void {
    const typeToSkin: Record<BoosterType, string> = {
      [BoosterType.Hammer]: 'hammer',
      [BoosterType.Move]: 'move',
      [BoosterType.Redraw]: 'redraw',
      [BoosterType.Undo]: 'undo',
    };

    const { boosterDescription, boosterName, boosterRays, boosterIconSlot } = this.layout;
    Object.entries(config.boostersDefinitions).forEach(([boosterType, def], index) => {
      const unlocked = config.levelNumber >= def.unlockLevel;

      if (unlocked) {
        boosterName[index].text = getLocalizedText(def.name);
        boosterDescription[index].text = getLocalizedText(def.description);
      } else {
        boosterName[index].text = getLocalizedText('Locked');
        boosterDescription[index].text = getLocalizedText('Reach Level <nr> to unlock').replace(
          '<nr>',
          `${def.unlockLevel}`,
        );
        boosterRays[index].visible = false;
      }

      const iconLayout = new BoosterButtonLayout();
      applyLayoutState(iconLayout, `${typeToSkin[boosterType as BoosterType]}-${unlocked ? 'enabled' : 'disabled'}`);
      iconLayout.indicator.visible = false;
      iconLayout.attractOverlay.visible = false;
      iconLayout.root.props({
        scale: 1.6,
        parent: boosterIconSlot[index],
      });
    });
  }
}
